import { useEffect, useState, type FunctionComponent } from "react";
import { Button, Card, Divider, Empty, message, Popconfirm, Table } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PerformingAction, programAutomationActions, RequestStatus } from "@fitness-app/app-store";
import { type ProgramAutomation } from "@fitness-app/data-models/entities/ProgramAutomation";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import ModalForm from "~/components/ModalForm/ModalForm";
import { analyticsService, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import AutomationTemplateForm from "./components/AutomationTemplateForm/AutomationTemplateForm";
import { type AutomationTemplateUpdateModel } from "./components/AutomationTemplateForm/types";

type MessageTemplateListItem = ProgramAutomation;
type EditedAutomation = AutomationTemplateUpdateModel & Pick<ProgramAutomation, "id">;

const useAutomationList = () => {
  const { t } = useTranslation(["settings", "automation"]);
  const [isFormVisible, setFormVisibility] = useState(false);
  const [editedAutomation, setEditedAutomation] = useState<EditedAutomation | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    void dispatch(programAutomationActions.fetchProgramAutomations());
  }, [dispatch]);

  const showForm = (automation?: EditedAutomation) => {
    setEditedAutomation(automation ?? null);
    setFormVisibility(true);
  };

  const hideForm = () => {
    setFormVisibility(false);
  };

  const handleSubmit = async (payload: ProgramAutomation) => {
    await dispatch(programAutomationActions.addProgramAutomation(payload));
    hideForm();
  };

  const handleUpdate = async (automation: AutomationTemplateUpdateModel) => {
    if (!editedAutomation) {
      return;
    }

    try {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({ automationId: editedAutomation.id, automation }),
      ).unwrap();
      analyticsService.track("program_automation_renamed", { automationId: editedAutomation.id });
      hideForm();
    } catch {
      void message.error(t("common:messages.error.default"));
      // the reducer applies the new name optimistically, so bring the saved one back
      void dispatch(programAutomationActions.fetchProgramAutomations());
    }
  };

  const duplicate = async (formData: ProgramAutomation) => {
    void message.loading(t("automation:duplicate"));
    const programForDuplicate = await dispatch(programAutomationActions.fetchProgramAutomation(formData.id)).unwrap();
    const duplicated = {
      ...programForDuplicate,
      name: `${formData.name} (1)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      id: generateUniqId(),
    };

    delete duplicated.initialSurvey;
    delete duplicated.additionalSurvey;

    await dispatch(programAutomationActions.addProgramAutomation(duplicated));
    message.destroy();
  };

  const deleteTemplate = async (id: string) => {
    void message.loading(t("automation:removing"));
    await dispatch(programAutomationActions.deleteProgramAutomation(id));
    void dispatch(programAutomationActions.fetchProgramAutomations());
    message.destroy();
  };

  return {
    model: {
      isFormVisible,
      editedAutomation,
    },
    operations: {
      showForm,
      hideForm,
      handleSubmit,
      handleUpdate,
      duplicate,
      deleteTemplate,
    },
  };
};

const AutomationList: FunctionComponent = () => {
  const { model, operations } = useAutomationList();
  const { t } = useTranslation(["automation", "common"]);
  const { listStatus, data, performingAction } = useAppSelector((store) => store.programAutomation);
  const navigate = useNavigate();

  return (
    <Card
      loading={listStatus === RequestStatus.FETCHING}
      bordered={false}
      extra={
        <Button type="primary" onClick={() => operations.showForm()}>
          {t("common:button.add")}
        </Button>
      }
    >
      <Table
        loading={false}
        dataSource={data}
        rowKey="id"
        pagination={{
          hideOnSinglePage: true,
        }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common:emptyList")} />,
        }}
      >
        <Table.Column<MessageTemplateListItem> title={t("programAutomation.list.name")} key="name" dataIndex="name" />
        <Table.Column<MessageTemplateListItem>
          title={t("programAutomation.list.duration")}
          key="duration"
          dataIndex="duration"
          render={(_, row) =>
            `${Object.keys(row.schedule).length} ${t("programAutomation.list.weeks", {
              count: Object.keys(row.schedule).length,
            })}`
          }
        />
        <Table.Column<MessageTemplateListItem>
          title={t("programAutomation.list.comment")}
          key="comment"
          dataIndex="comment"
          responsive={["md"]}
          ellipsis
          render={(text: string) => text || "-"}
        />
        <Table.Column<MessageTemplateListItem>
          title={t("programAutomation.list.options")}
          key="action"
          align="center"
          className="whitespace-nowrap"
          render={(_, model) => (
            <>
              <Button type="link" onClick={() => navigate(`/automation/details/${model.id}`)}>
                {t("common:details")}
              </Button>
              <Divider type={"vertical"} />
              <Button
                type="link"
                onClick={() => operations.showForm({ id: model.id, name: model.name, comment: model.comment ?? "" })}
              >
                {t("common:button.edit")}
              </Button>
              <Divider type={"vertical"} />
              <Button type="link" onClick={() => operations.duplicate(model)}>
                {t("common:button.duplicate")}
              </Button>
              <Divider type={"vertical"} />
              <Popconfirm
                title={t("programAutomation.list.delete")}
                okText={t("common:switch.yes")}
                cancelText={t("common:button.cancel")}
                placement="top"
                onConfirm={() => operations.deleteTemplate(model.id)}
              >
                <Button type="link">{t("common:button.delete")}</Button>
              </Popconfirm>
            </>
          )}
        />
      </Table>
      <ModalForm
        open={model.isFormVisible}
        onCancel={operations.hideForm}
        title={t(model.editedAutomation ? "programAutomation.form.update" : "programAutomation.form.title")}
        loading={performingAction === PerformingAction.Adding}
      >
        <AutomationTemplateForm
          onSubmit={operations.handleSubmit}
          onUpdate={operations.handleUpdate}
          model={model.editedAutomation ?? undefined}
        />
      </ModalForm>
    </Card>
  );
};

export default AutomationList;
